"use client";

import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, Clock, Mail, Phone, X } from "lucide-react";
import { useEffect, useState } from "react";
import AnimatedGradientBg from "./animated-gradient-bg";
import "./dashboard-preview.css";
import RentidLogo from "./../assets/images/rent-id-logo-dark.png";

export default function DashboardPreview() {
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [checkoutItems, setCheckoutItems] = useState([]);
  const [emailText, setEmailText] = useState("");
  const [phoneText, setPhoneText] = useState("");
  const [hoveredReviewCard, setHoveredReviewCard] = useState(null);
  const [_cursorY, setCursorY] = useState(100);
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const [isIdentityBlinking, setIsIdentityBlinking] = useState(false);
  const [isDriverLicenseBlinking, setIsDriverLicenseBlinking] = useState(false);
  const [isCriminalBlinking, setIsCriminalBlinking] = useState(false);
  const [isInsuranceBlinking, setIsInsuranceBlinking] = useState(false);
  const [showCursor, setShowCursor] = useState(false);
  const [_currentStep, setCurrentStep] = useState(0);
  const [isButtonClicked, setIsButtonClicked] = useState(false);

  const fullEmail = "john.renter@example.com";
  const fullPhone = "(555) 123-4567";

  useEffect(() => {
    const runAnimation = async () => {
      // Reset all states
      setSelectedProducts([]);
      setCheckoutItems([]);
      setEmailText("");
      setPhoneText("");
      setHoveredReviewCard(null);
      setCursorY(100);
      setIsButtonHovered(false);
      setIsIdentityBlinking(false);
      setIsDriverLicenseBlinking(false);
      setIsCriminalBlinking(false);
      setIsInsuranceBlinking(false);
      setCurrentStep(0);
      setShowCursor(false);

      await new Promise((resolve) => setTimeout(resolve, 500));

      setIsIdentityBlinking(true);
      setSelectedProducts(["identity"]);
      await new Promise((resolve) => setTimeout(resolve, 350));
      setIsIdentityBlinking(false);
      await new Promise((resolve) => setTimeout(resolve, 50));
      setCheckoutItems([
        { id: "identity", name: "Identification & Biometrics" },
      ]);
      setCurrentStep(1000);
      await new Promise((resolve) => setTimeout(resolve, 400));

      setIsDriverLicenseBlinking(true);
      setSelectedProducts(["identity", "driver-license"]);
      await new Promise((resolve) => setTimeout(resolve, 350));
      setIsDriverLicenseBlinking(false);
      await new Promise((resolve) => setTimeout(resolve, 50));
      setCheckoutItems([
        { id: "identity", name: "Identification & Biometrics" },
        { id: "driver-license", name: "Driver License Validity" },
      ]);
      setCurrentStep(2000);
      await new Promise((resolve) => setTimeout(resolve, 400));

      setIsCriminalBlinking(true);
      setSelectedProducts(["identity", "driver-license", "criminal"]);
      await new Promise((resolve) => setTimeout(resolve, 350));
      setIsCriminalBlinking(false);
      await new Promise((resolve) => setTimeout(resolve, 50));
      setCheckoutItems([
        { id: "identity", name: "Identification & Biometrics" },
        { id: "driver-license", name: "Driver License Validity" },
        { id: "criminal", name: "DMV Criminal Record Check" },
      ]);
      setCurrentStep(3000);
      await new Promise((resolve) => setTimeout(resolve, 400));

      setIsInsuranceBlinking(true);
      setSelectedProducts([
        "identity",
        "driver-license",
        "criminal",
        "insurance",
      ]);
      await new Promise((resolve) => setTimeout(resolve, 350));
      setIsInsuranceBlinking(false);
      await new Promise((resolve) => setTimeout(resolve, 50));
      setCheckoutItems([
        { id: "identity", name: "Identification & Biometrics" },
        { id: "driver-license", name: "Driver License Validity" },
        { id: "criminal", name: "DMV Criminal Record Check" },
        { id: "insurance", name: "Personal Insurance Validation" },
      ]);
      setCurrentStep(4000);
      await new Promise((resolve) => setTimeout(resolve, 500));

      for (let i = 0; i <= fullEmail.length; i++) {
        setEmailText(fullEmail.slice(0, i));
        await new Promise((resolve) => setTimeout(resolve, 35));
      }

      await new Promise((resolve) => setTimeout(resolve, 200));

      for (let i = 0; i <= fullPhone.length; i++) {
        setPhoneText(fullPhone.slice(0, i));
        await new Promise((resolve) => setTimeout(resolve, 35));
      }

      await new Promise((resolve) => setTimeout(resolve, 350));

      setCursorY(20);
      await new Promise((resolve) => setTimeout(resolve, 400));
      setIsButtonHovered(true);
      setShowCursor(true);
      await new Promise((resolve) => setTimeout(resolve, 200));

      setIsButtonClicked(true);
      await new Promise((resolve) => setTimeout(resolve, 200));
      setIsButtonClicked(false);

      await new Promise((resolve) => setTimeout(resolve, 300));
      setIsButtonHovered(false);
      setShowCursor(false);

      await new Promise((resolve) => setTimeout(resolve, 100));

      await new Promise((resolve) => setTimeout(resolve, 400));

      for (let i = 0; i < 5; i++) {
        setHoveredReviewCard(i);
        await new Promise((resolve) => setTimeout(resolve, 600));
      }

      setHoveredReviewCard(null);
      await new Promise((resolve) => setTimeout(resolve, 800));

      // Loop
      runAnimation();
    };

    runAnimation();
  }, []);

  return (
    <div className="r2-dashboard-preview">
      <div className="r2-dashboard-preview__frame">
        <AnimatedGradientBg />

        <div className="r2-dashboard-preview__content">
          {/* Left Side - Text Content */}
          <div className="r2-dashboard-preview__intro">
            <img
              alt=""
              src={RentidLogo}
              className="r2-dashboard-preview__logo"
            />
            <h1 className="r2-dashboard-preview__headline">
              Risk mitigation workflows managed automatically.
            </h1>

            <div className="r2-dashboard-preview__steps">
              <p className="r2-dashboard-preview__step">
                <span className="r2-dashboard-preview__step-number">1</span>
                <span>Select verification services</span>
              </p>
              <p className="r2-dashboard-preview__step">
                <span className="r2-dashboard-preview__step-number">2</span>
                <span>Add driver contact information</span>
              </p>
              <p className="r2-dashboard-preview__step">
                <span className="r2-dashboard-preview__step-number">3</span>
                <span>Confirm services and checkout </span>
              </p>
              <p className="r2-dashboard-preview__step">
                <span className="r2-dashboard-preview__step-number">4</span>
                <span>Review and manage drivers</span>
              </p>
            </div>
          </div>

          <div className="r2-dashboard-preview__workspace">
            <div className="r2-dashboard-preview__columns">
              <div className="r2-dashboard-preview__column">
                <div className="r2-dashboard-preview__card r2-dashboard-preview__card--tall">
                  <div className="r2-dashboard-preview__card-title-row">
                    <h3 className="r2-dashboard-preview__card-title">
                      Choose Products
                    </h3>
                  </div>
                  <div className="r2-dashboard-preview__product-list">
                    <motion.div
                      animate={{
                        backgroundColor: selectedProducts.includes("identity")
                          ? "rgb(250 245 255)"
                          : "rgb(248 250 252)",
                        borderColor: selectedProducts.includes("identity")
                          ? "rgb(216 180 254)"
                          : "rgb(226 232 240)",
                        opacity: isIdentityBlinking ? [1, 0.7, 1] : 1,
                      }}
                      transition={{
                        backgroundColor: { duration: 0.3 },
                        borderColor: { duration: 0.3 },
                        opacity: {
                          duration: 0.5,
                          repeat: isIdentityBlinking ? 1 : 0,
                        },
                      }}
                      className="r2-dashboard-preview__product"
                    >
                      {selectedProducts.includes("identity") ? (
                        <CheckCircle2 className="r2-dashboard-preview__product-icon r2-dashboard-preview__product-icon--purple" />
                      ) : (
                        <div className="r2-dashboard-preview__product-box" />
                      )}
                      <span
                        className={`r2-dashboard-preview__product-label ${
                          selectedProducts.includes("identity")
                            ? "r2-dashboard-preview__product-label--active"
                            : "r2-dashboard-preview__product-label--inactive"
                        }`}
                      >
                        Identification &amp; Biometrics
                      </span>
                    </motion.div>

                    <motion.div
                      animate={{
                        backgroundColor: selectedProducts.includes(
                          "driver-license"
                        )
                          ? "rgb(238 242 255)"
                          : "rgb(248 250 252)",
                        borderColor: selectedProducts.includes("driver-license")
                          ? "rgb(165 180 252)"
                          : "rgb(226 232 240)",
                        opacity: isDriverLicenseBlinking ? [1, 0.7, 1] : 1,
                      }}
                      transition={{
                        backgroundColor: { duration: 0.3 },
                        borderColor: { duration: 0.3 },
                        opacity: {
                          duration: 0.5,
                          repeat: isDriverLicenseBlinking ? 1 : 0,
                        },
                      }}
                      className="r2-dashboard-preview__product"
                    >
                      {selectedProducts.includes("driver-license") ? (
                        <CheckCircle2 className="r2-dashboard-preview__product-icon r2-dashboard-preview__product-icon--indigo" />
                      ) : (
                        <div className="r2-dashboard-preview__product-box" />
                      )}
                      <span
                        className={`r2-dashboard-preview__product-label ${
                          selectedProducts.includes("driver-license")
                            ? "r2-dashboard-preview__product-label--active"
                            : "r2-dashboard-preview__product-label--inactive"
                        }`}
                      >
                        Driver License Validity
                      </span>
                    </motion.div>

                    <motion.div
                      animate={{
                        backgroundColor: selectedProducts.includes("criminal")
                          ? "rgb(254 242 242)"
                          : "rgb(248 250 252)",
                        borderColor: selectedProducts.includes("criminal")
                          ? "rgb(252 165 165)"
                          : "rgb(226 232 240)",
                        opacity: isCriminalBlinking ? [1, 0.7, 1] : 1,
                      }}
                      transition={{
                        backgroundColor: { duration: 0.3 },
                        borderColor: { duration: 0.3 },
                        opacity: {
                          duration: 0.5,
                          repeat: isCriminalBlinking ? 1 : 0,
                        },
                      }}
                      className="r2-dashboard-preview__product"
                    >
                      {selectedProducts.includes("criminal") ? (
                        <CheckCircle2 className="r2-dashboard-preview__product-icon r2-dashboard-preview__product-icon--red" />
                      ) : (
                        <div className="r2-dashboard-preview__product-box" />
                      )}
                      <span
                        className={`r2-dashboard-preview__product-label ${
                          selectedProducts.includes("criminal")
                            ? "r2-dashboard-preview__product-label--active"
                            : "r2-dashboard-preview__product-label--inactive"
                        }`}
                      >
                        DMV Criminal Record Check
                      </span>
                    </motion.div>

                    <motion.div
                      animate={{
                        backgroundColor: selectedProducts.includes("mvr")
                          ? "rgb(255 255 255)"
                          : "rgb(248 250 252)",
                        borderColor: selectedProducts.includes("mvr")
                          ? "rgb(0 0 0)"
                          : "rgb(226 232 240)",
                        opacity: selectedProducts.includes("mvr") ? 1 : 1,
                      }}
                      transition={{
                        backgroundColor: { duration: 0.3 },
                        borderColor: { duration: 0.3 },
                        opacity: { duration: 0.5, repeat: 0 },
                      }}
                      className="r2-dashboard-preview__product"
                    >
                      {selectedProducts.includes("mvr") ? (
                        <CheckCircle2 className="r2-dashboard-preview__product-icon r2-dashboard-preview__product-icon--black" />
                      ) : (
                        <div className="r2-dashboard-preview__product-box" />
                      )}
                      <span
                        className={`r2-dashboard-preview__product-label ${
                          selectedProducts.includes("mvr")
                            ? "r2-dashboard-preview__product-label--active"
                            : "r2-dashboard-preview__product-label--inactive"
                        }`}
                      >
                        Motor Vehicle Report (MVR)
                      </span>
                    </motion.div>

                    <motion.div
                      animate={{
                        backgroundColor: selectedProducts.includes("insurance")
                          ? "rgb(240 253 244)"
                          : "rgb(248 250 252)",
                        borderColor: selectedProducts.includes("insurance")
                          ? "rgb(134 239 172)"
                          : "rgb(226 232 240)",
                        opacity: isInsuranceBlinking ? [1, 0.7, 1] : 1,
                      }}
                      transition={{
                        backgroundColor: { duration: 0.3 },
                        borderColor: { duration: 0.3 },
                        opacity: {
                          duration: 0.5,
                          repeat: isInsuranceBlinking ? 1 : 0,
                        },
                      }}
                      className="r2-dashboard-preview__product"
                    >
                      {selectedProducts.includes("insurance") ? (
                        <CheckCircle2 className="r2-dashboard-preview__product-icon r2-dashboard-preview__product-icon--green" />
                      ) : (
                        <div className="r2-dashboard-preview__product-box" />
                      )}
                      <span
                        className={`r2-dashboard-preview__product-label ${
                          selectedProducts.includes("insurance")
                            ? "r2-dashboard-preview__product-label--active"
                            : "r2-dashboard-preview__product-label--inactive"
                        }`}
                      >
                        Personal Insurance Validation
                      </span>
                    </motion.div>

                    <div className="r2-dashboard-preview__product r2-dashboard-preview__product--disabled">
                      <div className="r2-dashboard-preview__product-meta">
                        <div className="r2-dashboard-preview__product-box" />
                        <span className="r2-dashboard-preview__product-label r2-dashboard-preview__product-label--inactive">
                          Driver IQ Score
                        </span>
                      </div>
                      <span className="r2-dashboard-preview__badge">
                        Coming Soon
                      </span>
                    </div>
                  </div>
                </div>

                <div className="r2-dashboard-preview__card r2-dashboard-preview__card--short">
                  <div className="r2-dashboard-preview__card-title-row">
                    <h3 className="r2-dashboard-preview__card-title">
                      Add Contact{" "}
                    </h3>
                  </div>
                  <div className="r2-dashboard-preview__fields">
                    <div>
                      <label className="r2-dashboard-preview__field-label">
                        <Mail className="r2-dashboard-preview__field-icon" />
                        Email Address
                      </label>
                      <div className="r2-dashboard-preview__input">
                        <span className="r2-dashboard-preview__input-text">
                          {emailText}
                          {emailText && emailText.length < fullEmail.length && (
                            <span className="r2-dashboard-preview__caret">
                              |
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="r2-dashboard-preview__field-label">
                        <Phone className="r2-dashboard-preview__field-icon" />
                        Phone Number
                      </label>
                      <div className="r2-dashboard-preview__input">
                        <span className="r2-dashboard-preview__input-text">
                          {phoneText}
                          {phoneText && phoneText.length < fullPhone.length && (
                            <span className="r2-dashboard-preview__caret">
                              |
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="r2-dashboard-preview__column r2-dashboard-preview__column--reverse">
                <div className="r2-dashboard-preview__card r2-dashboard-preview__card--short r2-dashboard-preview__card--relative">
                  <div className="r2-dashboard-preview__card-title-row">
                    <h3 className="r2-dashboard-preview__card-title">
                      Checkout
                    </h3>
                    <AnimatePresence>
                      {checkoutItems.length > 0 && (
                        <motion.span
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.8 }}
                          transition={{ duration: 0.3 }}
                          className="r2-dashboard-preview__cart-badge"
                        >
                          Added to cart{" "}
                          {checkoutItems.length > 1 &&
                            `(${checkoutItems.length})`}
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </div>

                  <div className="r2-dashboard-preview__cart-list">
                    <AnimatePresence>
                      {checkoutItems.map((item, index) => (
                        <motion.div
                          key={item.id}
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: index * 0.2 }}
                          className="r2-dashboard-preview__cart-item"
                        >
                          <div className="r2-dashboard-preview__cart-item-left">
                            {item.id === "identity" && (
                              <CheckCircle2 className="r2-dashboard-preview__cart-icon r2-dashboard-preview__cart-icon--purple" />
                            )}
                            {item.id === "driver-license" && (
                              <CheckCircle2 className="r2-dashboard-preview__cart-icon r2-dashboard-preview__cart-icon--indigo" />
                            )}
                            {item.id === "criminal" && (
                              <CheckCircle2 className="r2-dashboard-preview__cart-icon r2-dashboard-preview__cart-icon--red" />
                            )}
                            {item.id === "insurance" && (
                              <CheckCircle2 className="r2-dashboard-preview__cart-icon r2-dashboard-preview__cart-icon--green" />
                            )}
                            {item.id === "mvr" && (
                              <CheckCircle2 className="r2-dashboard-preview__cart-icon r2-dashboard-preview__cart-icon--black" />
                            )}
                            <span className="r2-dashboard-preview__cart-item-name">
                              {item.name}
                            </span>
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>

                  <div className="r2-dashboard-preview__checkout-footer">
                    <motion.button
                      animate={{
                        scale: isButtonClicked ? 0.95 : isButtonHovered ? 1.02 : 1,
                        boxShadow: isButtonHovered
                          ? "0 8px 24px rgba(139, 92, 246, 0.4)"
                          : "0 4px 12px rgba(139, 92, 246, 0.2)",
                      }}
                      transition={{ duration: 0.2 }}
                      className="r2-dashboard-preview__checkout-button"
                    >
                      Complete Payment
                    </motion.button>
                  </div>

                  <AnimatePresence>
                    {showCursor && (
                      <motion.div
                        initial={{ opacity: 0, top: 80 }}
                        animate={{
                          opacity: 1,
                          top: isButtonHovered ? 200 : 80,
                          left: "50%",
                          x: "-50%",
                        }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4, ease: "easeInOut" }}
                        className="r2-dashboard-preview__cursor"
                      >
                        <svg
                          width="28"
                          height="28"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className="r2-dashboard-preview__cursor-icon"
                        >
                          <path
                            d="M5 3L19 12L12 13L9 19L5 3Z"
                            fill="white"
                            stroke="black"
                            strokeWidth="1"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <div className="r2-dashboard-preview__card r2-dashboard-preview__card--tall r2-dashboard-preview__card--overflow">
                  <div className="r2-dashboard-preview__card-title-row">
                    <h3 className="r2-dashboard-preview__card-title">
                      Review Status
                    </h3>
                  </div>
                  <div className="r2-dashboard-preview__review-list">
                    {[
                      {
                        theme: "approved",
                        iconTheme: "green",
                        icon: CheckCircle2,
                        title: "Identification & Biometrics",
                        status: "Approved •  2 min ago",
                        statusTheme: "approved",
                        index: 0,
                      },
                      {
                        theme: "failed",
                        iconTheme: "red",
                        icon: X,
                        title: "Driver License Validity",
                        status: "Failed •  Action Required",
                        statusTheme: "failed",
                        index: 1,
                      },
                      {
                        theme: "caution",
                        iconTheme: "yellow",
                        icon: Clock,
                        title: "DMV Criminal Record Check",
                        status: "Caution - Criminal Activity Detected",
                        statusTheme: "caution",
                        index: 2,
                      },
                      {
                        theme: "approved",
                        iconTheme: "green",
                        icon: CheckCircle2,
                        title: "Personal Insurance Validation",
                        status: "Approved •  Just now",
                        statusTheme: "approved",
                        index: 3,
                      },
                      {
                        theme: "neutral",
                        iconTheme: "black",
                        icon: CheckCircle2,
                        title: "Motor Vehicle Report (MVR)",
                        status: "Approved •  Just now",
                        statusTheme: "neutral",
                        index: 4,
                      },
                    ].map((item) => {
                      const Icon = item.icon;
                      const isHovered = hoveredReviewCard === item.index;

                      return (
                        <motion.div
                          key={item.index}
                          animate={{
                            scale: isHovered ? 1.05 : 1,
                            filter: isHovered
                              ? "brightness(1.1)"
                              : "brightness(1)",
                            boxShadow: isHovered
                              ? "0 8px 20px rgba(0,0,0,0.25)"
                              : "0 0 0 rgba(0,0,0,0)",
                          }}
                          transition={{ duration: 0.3, ease: "easeOut" }}
                          className={`r2-dashboard-preview__review-item r2-dashboard-preview__review-item--${item.theme}`}
                        >
                          <div
                            className={`r2-dashboard-preview__review-icon r2-dashboard-preview__review-icon--${item.iconTheme}`}
                          >
                            <Icon className="r2-dashboard-preview__review-icon-svg" />
                          </div>
                          <div className="r2-dashboard-preview__review-body">
                            <div className="r2-dashboard-preview__review-title">
                              {item.title}
                            </div>
                            <div
                              className={`r2-dashboard-preview__review-status r2-dashboard-preview__review-status--${item.statusTheme}`}
                            >
                              {item.status}
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
